import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAlert } from 'react-alert';
import api from '../api/api';
import { axios } from '../api/axios';
import MainLoading from '../view/components/loading/main-loading';

export const AdminContext = React.createContext({});

const intervalTimeout = 10000;

function AdminProvider(props) {
  const [auth, setAuth] = useState({ value: false, status: 0 });
  const [events, setEvents] = useState({ list: [], unseenCount: 0 });
  const [checked, setChecked] = useState(false);
  let history = useHistory();
  const alert = useAlert();
  let interval = useRef(null);
  const authRequestGeneration = useRef(0);

  useEffect(() => {
    if (interval.current) {
      clearInterval(interval.current);
    }

    const handleInterval = () => {
      const requestGeneration = ++authRequestGeneration.current;
      api.admin.auth().then((response) => {
        if (requestGeneration !== authRequestGeneration.current) {
          return;
        }

        if (response.auth) {
          if (response.auth.value === false) {
            setEvents({ list: [], unseenCount: 0 });
            if (interval.current) {
              clearInterval(interval.current);
            }

            localStorage.removeItem('admin-auth-token');
            axios.defaults.headers.common['Admin-Auth-Token'] = '';
          } else if (response.events !== undefined) {
            const { list, unseenCount } = response.events;
            setEvents((prevEvents) => {
              const update = {
                list: list,
                unseenCount: unseenCount,
              };

              return { ...prevEvents, ...update };
            });
          }

          // <Set auth if data changed>
          if (auth.value !== response.auth.value) {
            setAuth(response.auth);
          }
          // </Set auth if data changed>
        }

        setChecked(true);
      });
    };

    if (auth.value) {
      interval.current = setInterval(handleInterval, intervalTimeout);
    } else if (checked === false) {
      handleInterval();
    }

    return () => {
      if (interval.current) {
        clearInterval(interval.current);
      }
    };
  }, [auth.value]);

  function login(data) {
    authRequestGeneration.current += 1;
    // if admin is logged and auth is true then set interval and token.
    if (data.auth.value === true) {
      localStorage.setItem('admin-auth-token', data.auth.authToken);
      axios.defaults.headers.common['Admin-Auth-Token'] = data.auth.authToken;
    }

    if (data.events) {
      setEvents(data.events);
    }

    setAuth(data.auth);
  }

  function logout() {
    authRequestGeneration.current += 1;
    if (interval.current) {
      clearInterval(interval.current);
    }

    api.admin.logout().then((response) => {
      if (response.status === 'ok') {
        localStorage.removeItem('admin-auth-token');
        axios.defaults.headers.common['Admin-Auth-Token'] = '';
        setAuth({ value: false, status: 0 });
        setEvents({ list: [], unseenCount: 0 });
      }
    });
  }

  // Event manager => em
  const em = {};
  em.list = events.list;
  em.unseenCount = events.unseenCount;
  em.eventSeen = (data) => {
    // <set (seen === 1) for event>
    const section = data.section;
    const sectionDependency = data.section_dependency;
    const eventId = parseInt(data.id, 10);

    setEvents((prevEvents) => {
      const _list = prevEvents.list.map((item) => {
        if (section === 'direct') {
          if (item.section === 'direct' && String(item.section_dependency) === String(sectionDependency)) {
            return { ...item, seen: 1 };
          }
          return item;
        }

        if (item.section === section && parseInt(item.id, 10) === eventId) {
          return { ...item, seen: 1 };
        }

        return item;
      });

      return { ...prevEvents, list: _list };
    });
    // </set (seen === 1) for event>

    api.admin.eventSeen(data).then((response) => {
      const { status } = response;

      if (status === 'ok') {
        setEvents((prevEvents) => {
          const update = {
            unseenCount: Math.max(0, parseInt(prevEvents.unseenCount, 10) - 1),
          };

          return { ...prevEvents, ...update };
        });
      }
    });
  };
  em.eventsSeen = () => {
    if (events.unseenCount > 0) {
      api.admin.allEventSeen().then((response) => {
        const { status } = response;

        if (status === 'ok') {
          setEvents((prevEvents) => {
            const update = {
              unseenCount: 0,
            };

            return { ...prevEvents, ...update };
          });
        }
      });
    }
  };
  // --Event manager => em

  // Password recovery manager => prm
  const prm = {};
  [prm.message, prm.setMessage] = useState(null);
  [prm.pending, prm.setPending] = useState(false);
  [prm.form, prm.setForm] = useState('username');
  [prm.destination, prm.setDestination] = useState('');
  prm.reset = () => {
    prm.setForm('username');
    prm.setDestination('');
    prm.setPending(false);
    prm.setMessage(null);
  };
  prm.sendCode = (username) => {
    prm.setPending(true);

    api.admin
      .sendPasswordRecoveryCode({ username })
      .then((response) => {
        const { status, message, destination } = response;

        if (status === 'ok') {
          prm.setMessage(null);
          prm.setDestination(destination);
          prm.setForm('code');
        } else if (status === 'error') {
          prm.setMessage(message);
        }

        prm.setPending(false);
      })
      .catch(() => {
        prm.setPending(false);
      });
  };
  prm.resendCode = () => {
    prm.setPending(true);

    api.admin
      .resendPasswordRecoveryCode()
      .then((response) => {
        const { status, message, reset } = response;

        if (status === 'ok') {
          alert.success(message);
        } else if (status === 'error') {
          alert.error(message);
        }

        if (reset) {
          prm.reset();
        }

        prm.setPending(false);
      })
      .catch(() => {
        prm.setPending(false);
      });
  };
  prm.validateCode = (code) => {
    prm.setPending(true);

    api.admin
      .validationPasswordRecoveryCode({ code })
      .then((response) => {
        const { status, message, reset } = response;

        if (status === 'ok') {
          prm.setMessage(null);
          prm.setForm('password');
        } else if (status === 'error') {
          if (reset) {
            alert.error(message);
          } else {
            prm.setMessage(message);
          }
        }

        if (reset) {
          prm.reset();
        }

        prm.setPending(false);
      })
      .catch(() => {
        prm.setPending(false);
      });
  };
  prm.changePassword = (data) => {
    prm.setPending(true);

    api.admin
      .setNewPassword(data)
      .then((response) => {
        const { status, message, reset } = response;

        if (status === 'ok') {
          alert.success(message);

          history.replace({
            pathname: '/login',
          });
        } else {
          if (status === 'error') {
            if (reset) {
              alert.error(message);
            } else {
              prm.setMessage(message);
            }
          }

          if (reset) {
            prm.reset();
          }
        }

        prm.setPending(false);
      })
      .catch(() => {
        prm.setPending(false);
      });
  };
  // --Password recovery manager => prm

  const adminContext = {
    auth,
    passwordRecovery: prm,
    events: em,
    login,
    logout,
  };

  if (checked) {
    return <AdminContext.Provider value={adminContext}>{props.children}</AdminContext.Provider>;
  } else {
    return <MainLoading />;
  }
}

export default AdminProvider;
